import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from 'react-query';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';
import { useAuthUser } from 'hooks/useAuthUser';
import { SupportedCategories } from './types';
import { ProductCategory } from 'screens/AccessExpressV3/accessExpress.types';
import { CartStore } from 'screens/AccessExpressV3/CartProvider';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import { postQuoteToRequest } from '../utils';
import { DIA_REFETCH_INTERVAL } from '../AccessProductDetails';

type QuoteIDs = Record<SupportedCategories, string | undefined>;
const categories: SupportedCategories[] = ['Broadband', 'DIA', 'Starlink'];

const getCartQuoteIDs = (cart: CartStore): QuoteIDs => ({
  Broadband: cart.additionalDetails?.bb_quote_request,
  DIA: cart.additionalDetails?.dia_quote_request,
  Starlink: cart.additionalDetails?.starlink_quote_request,
});

export const useAccessCategoryPreloader = (run?: boolean) => {
  const queryClient = useQueryClient();
  const cart = useCartContext();
  const userInfo = useAuthUser();
  const [quoteIDs, setQuoteIDs] = useState<QuoteIDs>(() =>
    getCartQuoteIDs(cart),
  );
  const workingRef = useRef(false);

  useEffect(() => {
    if (!run || workingRef.current) return;

    const currentQuoteIDs = getCartQuoteIDs(cart);
    setQuoteIDs(currentQuoteIDs);

    const missing = categories.filter((t) => !currentQuoteIDs[t]);
    if (missing.length === 0) return;

    workingRef.current = true;

    (async () => {
      for (const type of missing) {
        const id = await handleAccessCategorySelection({
          type,
          cart,
          userInfo,
          setStartPolling: cart.setStartPolling,
          setLoadingDraftQuote: cart.setLoadingDraftQuote,
          currentQuoteIDs,
          queryClient,
        });

        if (id) {
          setQuoteIDs((prev) => ({ ...prev, [type]: id }));
        }
      }
      workingRef.current = false;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, cart.additionalDetails]);

  return quoteIDs;
};

export const handleAccessCategorySelection = async ({
  type,
  cart,
  userInfo,
  setStartPolling,
  setLoadingDraftQuote,
  currentQuoteIDs,
  queryClient,
}: {
  type: ProductCategory;
  cart: CartStore;
  userInfo: ReturnType<typeof useAuthUser>;
  setStartPolling: (val: boolean) => void;
  currentQuoteIDs: QuoteIDs;
  queryClient: QueryClient;
  setLoadingDraftQuote: (val: boolean) => void;
}): Promise<string | undefined> => {
  const already =
    currentQuoteIDs[type as SupportedCategories] ||
    getCartQuoteIDs(cart)[type as SupportedCategories];
  if (already) return already;

  if (type === 'DIA') cart.setDIAFlowActive(true);

  const cartAdditionalDetails = cart.additionalDetails;

  try {
    const quoteResponse = await postQuote(
      postQuoteToRequest(userInfo, cart.locations, [
        { product_type: type, ...cartAdditionalDetails?.configs },
      ]),
    );

    const quoteResultIdConfig =
      type === 'Starlink'
        ? { starlink_quote_request: quoteResponse.id }
        : type === 'DIA'
          ? { dia_quote_request: quoteResponse.id }
          : { bb_quote_request: quoteResponse.id };

    const additionalDetails = {
      ...cartAdditionalDetails,
      ...currentQuoteIDs,
      ...quoteResultIdConfig,
    };

    const draft = await updateOrder(
      { additional_details: additionalDetails },
      cart.draftId ?? '',
    );

    cart.updateStateFromDraft(draft);

    handlePostQuoteStatus({
      category: type,
      status: quoteResponse.status,
      quoteId: quoteResponse.id,
      setStartPolling,
      queryClient,
      setLoadingDraftQuote,
    });

    return quoteResponse.id;
  } catch (err) {
    console.error('Error creating quote:', err);
    return undefined;
  }
};

const handlePostQuoteStatus = ({
  category,
  status,
  quoteId,
  setStartPolling,
  setLoadingDraftQuote,
  queryClient,
}: {
  category: ProductCategory;
  status: string;
  quoteId: string;
  setStartPolling: (val: boolean) => void;
  setLoadingDraftQuote: (val: boolean) => void;
  queryClient: QueryClient;
}) => {
  if (category === 'DIA') {
    if (status === 'Checking availability') {
      setTimeout(() => setStartPolling(true), DIA_REFETCH_INTERVAL);
    } else if (status === 'Closed') {
      setStartPolling(false);
    }
  } else {
    queryClient.refetchQueries(['access_products', quoteId, category]);
    setLoadingDraftQuote(false);
  }
};
